import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, Button, IconButton, makeStyles, Typography } from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import ConfirmationNumberOutlinedIcon from "@material-ui/icons/ConfirmationNumberOutlined";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { ContentPageStyles } from "../RoyalStarfish/styles";
import TicketEventsHeader from "./TicketEventsHeader";
import services from "../../services";
import { isWithinEasternWindow } from "./ticketProofUtils";
import ProofOfTicket from "./ProofOfTicket";

const HERO_JPG = "/images/Ticket-Holder.jpg";
const FOOTER_JPG = "/images/THP---Example.jpg";

const TH_ICON = (n) => `/images/${encodeURIComponent(`TH Icons-0${n}.png`)}`;

const HEADER_SOLID_AFTER = 40;

const useStyles = makeStyles((theme) => ({
  ...ContentPageStyles(theme),
  root: {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    backgroundColor: "#000",
    color: "#fff",
  },
  heroBleed: {
    position: "relative",
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  hero: {
    position: "relative",
    height: "100vh",
    minHeight: "100vh",
    overflow: "hidden",
    isolation: "isolate",
    transform: "translateZ(0)",
    backfaceVisibility: "hidden",
  },
  heroMedia: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    overflow: "hidden",
    transform: "translateZ(0)",
  },
  heroBgImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,35,90,0.45) 38%, rgba(0,91,200,0.55) 72%, rgba(0,123,255,0.5) 100%)",
  },
  heroUi: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    boxSizing: "border-box",
    padding: theme.spacing(2),
    paddingTop: 88,
    transform: "translateZ(0)",
    backfaceVisibility: "hidden",
    pointerEvents: "none",
    [theme.breakpoints.down("xs")]: {
      paddingTop: 80,
    },
  },
  heroUiCenter: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 0,
    width: "100%",
    pointerEvents: "auto",
  },
  heroUiBottom: {
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    paddingBottom: theme.spacing(2),
    pointerEvents: "auto",
  },
  heroScrollBtn: {
    color: "#fff",
    padding: theme.spacing(1.5),
    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.4))",
    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
    "& svg": {
      fontSize: 42,
    },
  },
  heroContent: {
    textAlign: "center",
    padding: theme.spacing(2, 3),
    maxWidth: 1000,
    marginLeft: "auto",
    marginRight: "auto",
    width: "100%",
    minWidth: 0,
  },
  heroHeadline: {
    color: "#fff",
    fontWeight: 700,
    letterSpacing: "0.03em",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontSize: "clamp(2.25rem, 6.5vw, 3.85rem)",
    lineHeight: 1.12,
    margin: 0,
    marginBottom: theme.spacing(1.5),
    textShadow: "0 2px 24px rgba(0,0,0,0.5)",
  },
  heroTagline: {
    color: "rgba(255,255,255,0.95)",
    fontSize: "clamp(1.2rem, 3.25vw, 1.75rem)",
    fontStyle: "italic",
    fontWeight: 400,
    lineHeight: 1.5,
    margin: 0,
    textShadow: "0 1px 16px rgba(0,0,0,0.5)",
  },
  proofButtonWrap: {
    marginTop: theme.spacing(3.5),
    display: "flex",
    justifyContent: "center",
  },
  proofButton: {
    backgroundColor: "#fff",
    color: "#000",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 700,
    fontSize: "clamp(1rem, 2.25vw, 1.25rem)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: theme.spacing(1.5, 4),
    borderRadius: 999,
    boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
    "&:hover": {
      backgroundColor: "#f1f1f1",
    },
  },
  proofButtonIcon: {
    marginRight: theme.spacing(1),
  },
  sectionBlue: {
    backgroundColor: "#007BFF",
    color: "#fff",
    padding: theme.spacing(6, 2),
    textAlign: "center",
  },
  sectionBlack: {
    backgroundColor: "#000",
    backgroundImage: "url(/images/Black.png)",
    backgroundRepeat: "repeat",
    color: "#fff",
    padding: theme.spacing(6, 2),
  },
  whatHowSection: {
    backgroundColor: "#000",
    backgroundImage: "url(/images/Black.png)",
    backgroundRepeat: "repeat",
    color: "#fff",
    padding: theme.spacing(3, 2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2.5, 1.5),
    },
  },
  sectionInner: {
    maxWidth: 960,
    marginLeft: "auto",
    marginRight: "auto",
  },
  weekendTitle: {
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 700,
    fontSize: "clamp(1.65rem, 4.25vw, 2.5rem)",
    marginBottom: theme.spacing(2),
  },
  bodyCenter: {
    lineHeight: 1.65,
    fontSize: "1.1875rem",
    maxWidth: 880,
    marginLeft: "auto",
    marginRight: "auto",
  },
  whatHeading: {
    textAlign: "center",
    fontWeight: 700,
    fontSize: "clamp(1.75rem, 3.5vw, 2.35rem)",
    marginBottom: theme.spacing(2),
    marginTop: 0,
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
  },
  iconRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    maxWidth: "min(1400px, 100%)",
    marginLeft: "auto",
    marginRight: "auto",
    [theme.breakpoints.up("md")]: {
      gap: theme.spacing(3),
    },
  },
  iconItem: {
    flex: "1 1 260px",
    maxWidth: 400,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  iconImgFrame: {
    width: 340,
    height: 340,
    maxWidth: "100%",
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: theme.spacing(1.25),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: 280,
      height: 280,
    },
    [theme.breakpoints.down("xs")]: {
      width: 240,
      height: 240,
      marginBottom: theme.spacing(1),
    },
  },
  iconImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
    transform: "scale(1.42)",
    transformOrigin: "center center",
    willChange: "transform",
  },
  whatHowDivider: {
    display: "block",
    width: "100%",
    maxWidth: 1100,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: 0,
    height: 1,
    minHeight: 1,
    border: "none",
    backgroundColor: "rgba(255,255,255,0.38)",
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(2.5),
    },
  },
  iconCaption: {
    fontSize: "1.1875rem",
    lineHeight: 1.55,
    color: "rgba(255,255,255,0.95)",
  },
  howHeading: {
    textAlign: "center",
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    marginTop: 0,
    fontSize: "clamp(1.5rem, 3vw, 1.9rem)",
  },
  importantBlock: {
    backgroundColor: "#007BFF",
    color: "#fff",
    padding: theme.spacing(6, 2),
    textAlign: "center",
  },
  importantTitle: {
    fontWeight: 800,
    letterSpacing: "0.06em",
    marginBottom: theme.spacing(2),
    fontSize: "clamp(1.35rem, 2.5vw, 1.75rem)",
  },
  importantSub: {
    fontWeight: 700,
    fontSize: "clamp(1.35rem, 2.5vw, 1.6rem)",
    marginBottom: theme.spacing(2),
  },
  footerHeroBleed: {
    position: "relative",
    minHeight: "100vh",
    width: "100%",
  },
  footerHero: {
    position: "relative",
    height: "100vh",
    minHeight: "100vh",
    overflow: "hidden",
    isolation: "isolate",
    transform: "translateZ(0)",
  },
  footerHeroMedia: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    overflow: "hidden",
  },
  footerBg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
  },
  postHeroTarget: {
    scrollMarginTop: 72,
    [theme.breakpoints.down("xs")]: { scrollMarginTop: 56 },
  },
}));

function TicketHolder() {
  const classes = useStyles();
  const { t } = useTranslation();
  const postHeroRef = useRef(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [proofSettings, setProofSettings] = useState(null);
  const [proofButtonVisible, setProofButtonVisible] = useState(false);
  const [proofModalOpen, setProofModalOpen] = useState(false);

  useEffect(() => {
    const updateHeader = () => {
      const y =
        window.scrollY ??
        document.documentElement.scrollTop ??
        document.body.scrollTop ??
        0;
      setHeaderScrolled(y >= HEADER_SOLID_AFTER);
    };
    updateHeader();
    window.addEventListener("scroll", updateHeader, { passive: true });
    return () => window.removeEventListener("scroll", updateHeader);
  }, []);

  useEffect(() => {
    let cancelled = false;
    services.Events.ticketProofSettings()
      .then((res) => {
        if (!cancelled) setProofSettings(res || {});
      })
      .catch(() => {
        if (!cancelled) setProofSettings({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!proofSettings) {
      setProofButtonVisible(false);
      return undefined;
    }
    const evaluate = () => {
      setProofButtonVisible(
        isWithinEasternWindow(proofSettings.start, proofSettings.end),
      );
    };
    evaluate();
    // Re-evaluate every 30 seconds so the button flips in/out of view near
    // the boundaries of the admin-configured window without a page reload.
    const id = window.setInterval(evaluate, 30000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") evaluate();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [proofSettings]);

  const handleOpenProof = () => setProofModalOpen(true);
  const handleCloseProof = () => setProofModalOpen(false);

  const scrollDown = () => {
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    const target = postHeroRef.current;
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({
        behavior: smooth ? "smooth" : "auto",
        block: "start",
      });
      return;
    }
    const root = document.scrollingElement ?? document.documentElement;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const maxScroll = Math.max(0, root.scrollHeight - root.clientHeight);
    root.scrollTop = Math.min(root.scrollTop + vh, maxScroll);
  };

  return (
    <div className={classes.root}>
      <TicketEventsHeader scrolled={headerScrolled} />

      <section className={classes.heroBleed}>
        <div className={classes.hero}>
          <div className={classes.heroMedia}>
            <img
              src={HERO_JPG}
              alt=""
              className={classes.heroBgImage}
              decoding="async"
            />
            <div className={classes.heroOverlay} aria-hidden />
          </div>
          <div className={classes.heroUi}>
            <div className={classes.heroUiCenter}>
              <div className={classes.heroContent}>
                <Typography className={classes.heroHeadline} component="h1">
                  {t("ticketHolderHeroTitle")}
                </Typography>
                <Typography className={classes.heroTagline} component="p">
                  {t("ticketHolderTagline")}
                </Typography>
                {proofButtonVisible && (
                  <div className={classes.proofButtonWrap}>
                    <Button
                      type="button"
                      variant="contained"
                      className={classes.proofButton}
                      onClick={handleOpenProof}
                      startIcon={
                        <ConfirmationNumberOutlinedIcon
                          className={classes.proofButtonIcon}
                        />
                      }
                    >
                      Show Proof of Ticket
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className={classes.heroUiBottom}>
              <IconButton
                type="button"
                className={classes.heroScrollBtn}
                aria-label={t("ticketHolderScrollDown")}
                onClick={scrollDown}
                size="medium"
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={postHeroRef}
        className={clsx(classes.sectionBlue, classes.postHeroTarget)}
      >
        <div className={classes.sectionInner}>
          <Typography className={classes.weekendTitle} component="h2">
            {t("ticketHolderWeekendSubtitle")}
          </Typography>
          <Typography className={classes.bodyCenter} component="p">
            {t("ticketHolderIntro")}
          </Typography>
          <Box mt={2}>
            <Typography className={classes.bodyCenter} component="p">
              {t("ticketHolderIntroClosing")}
            </Typography>
          </Box>
        </div>
      </section>

      <section className={classes.whatHowSection}>
        <Typography className={classes.whatHeading} component="h2">
          {t("ticketHolderWhatToExpectHeading")}
        </Typography>
        <div className={classes.iconRow}>
          {[1, 2, 3].map((n) => (
            <div key={n} className={classes.iconItem}>
              <Box className={classes.iconImgFrame}>
                <img src={TH_ICON(n)} alt="" className={classes.iconImg} />
              </Box>
              <Typography className={classes.iconCaption}>
                {t(`ticketHolderExpect${n}`)}
              </Typography>
            </div>
          ))}
        </div>

        <Box component="hr" className={classes.whatHowDivider} aria-hidden />

        <div className={classes.sectionInner}>
          <Typography className={classes.howHeading} component="h2">
            {t("ticketHolderHowToAttendHeading")}
          </Typography>
          <Typography
            className={classes.bodyCenter}
            align="center"
            component="p"
          >
            {t("ticketHolderHowToAttendBody")}
          </Typography>
        </div>
      </section>

      <section className={classes.importantBlock}>
        <div className={classes.sectionInner}>
          <Typography className={classes.importantTitle} component="h1">
            {t("ticketHolderImportantHeading")}
          </Typography>
          <Typography className={classes.importantSub} component="h3">
            {t("ticketHolderConfirmationHeading")}
          </Typography>
          <Typography
            className={classes.bodyCenter}
            component="div"
            style={{ whiteSpace: "pre-line" }}
          >
            {t("ticketHolderConfirmationBody")}
          </Typography>
        </div>
      </section>

      <section className={classes.footerHeroBleed}>
        <div className={classes.footerHero}>
          <div className={classes.footerHeroMedia}>
            <img
              src={FOOTER_JPG}
              alt=""
              className={classes.footerBg}
              decoding="async"
            />
            <div
              className={classes.heroOverlay}
              style={{ opacity: 0.85 }}
              aria-hidden
            />
          </div>
        </div>
      </section>

      {proofModalOpen && <ProofOfTicket onClose={handleCloseProof} />}
    </div>
  );
}

export default observer(TicketHolder);
